import {
  type FunctionalComponent,
  inject,
  type InjectionKey,
  onBeforeUnmount,
  type Ref,
  ref,
  unref,
} from 'vue';
import { onBeforeRouteLeave, type RouteLocationRaw } from 'vue-router';
import type { MaybeRef } from '@vueuse/core';

export interface Breadcrumb {
  label: string | FunctionalComponent;
  link: RouteLocationRaw;
}

export interface LinkTitle {
  text: string | FunctionalComponent;
  link: RouteLocationRaw;
}

export type Title = string | LinkTitle;

export interface HeaderProvider {
  extraBreadcrumbs: MaybeRef<Breadcrumb>[];
  title: MaybeRef<Title>[];
  globalFilterComponent?: {
    component: FunctionalComponent;
    props: Record<string, unknown>;
    events: Record<string, (e: never) => void>
  }[]
}

export interface AdditionalHeader {
  extraBreadcrumbs: MaybeRef<Breadcrumb>[];
  title?: MaybeRef<Title>;
  globalFilterComponent?: {
    component: FunctionalComponent;
    props: Record<string, unknown>;
    events: Record<string, (e: never) => void>
  }
}

export const headerKey = Symbol('jk/header') as InjectionKey<HeaderProvider>;

export const useHeader = () => inject(headerKey);

export const syncHeader = <T>(value: Ref<T>, updateFn: (v: T) => AdditionalHeader) => {
  const header = useHeader();

  const alreadyRemoved = ref(false);

  const sync = (val: T) => {
    if (header) {
      const next = updateFn(val);
      header.extraBreadcrumbs = header.extraBreadcrumbs.concat(next.extraBreadcrumbs);
      if (next.title) {
        header.title.push(next.title);
      }
      if (next.globalFilterComponent) {
        if (!header.globalFilterComponent) {
          header.globalFilterComponent = [];
        }
        header.globalFilterComponent?.push(next.globalFilterComponent);
      } else {
        header.globalFilterComponent?.push(header.globalFilterComponent[header.globalFilterComponent.length - 1]);
      }
      alreadyRemoved.value = false;
    }
  };

  sync(unref(value));

  const reset = () => {
    if (header && !alreadyRemoved.value) {
      header.extraBreadcrumbs.pop();
      header.title?.pop();
      header.globalFilterComponent?.pop();
      alreadyRemoved.value = true;
    }
  };

  onBeforeRouteLeave(reset);
  onBeforeUnmount(reset);

  return (val: T) => {
    reset();
    sync(val);
  };
};

export const activeTrackerKey = Symbol('jk/nav-active-tracker') as InjectionKey<{
  counter: number;
}>;
